/**
 * uploadReceipt — shared receipt/photo uploader with client-side compression.
 *
 * Flow:
 *  1. If the file is an image, compress to max 1920px @ JPEG 0.82 → typically
 *     drops a 6MB phone photo to <500KB.
 *  2. POST as multipart/form-data to /api/upload (avoids base64 JSON bloat that
 *     was exceeding the 16MB FastAPI default body limit on production).
 *
 * Returns: { file_id, url, file_name } or throws.
 */
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_DIMENSION = 1920;
const JPEG_QUALITY = 0.82;

const compressImage = (file) =>
  new Promise((resolve, reject) => {
    if (!file.type?.startsWith("image/")) {
      resolve(file);
      return;
    }
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("Couldn't read image"));
    reader.onload = (e) => {
      const img = new Image();
      img.onerror = () => resolve(file); // fall back to original
      img.onload = () => {
        const ratio = Math.min(1, MAX_DIMENSION / Math.max(img.width, img.height));
        // If the image is already smaller than max + small enough, skip compression
        if (ratio === 1 && file.size < 1.5 * 1024 * 1024) {
          resolve(file);
          return;
        }
        const w = Math.round(img.width * ratio);
        const h = Math.round(img.height * ratio);
        const canvas = document.createElement("canvas");
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, w, h);
        canvas.toBlob(
          (blob) => {
            if (!blob) {
              resolve(file);
              return;
            }
            // Wrap blob back into File so backend gets a filename
            const newName = file.name.replace(/\.(heic|heif|png|webp|tiff)$/i, ".jpg");
            resolve(new File([blob], newName, { type: "image/jpeg", lastModified: Date.now() }));
          },
          "image/jpeg",
          JPEG_QUALITY
        );
      };
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  });

export const uploadReceipt = async (file, { onProgress } = {}) => {
  if (!file) throw new Error("No file selected");

  // Hard limit before compression (50MB)
  if (file.size > 50 * 1024 * 1024) {
    throw new Error("File too large (max 50MB)");
  }

  const compressed = await compressImage(file);

  const fd = new FormData();
  fd.append("file", compressed);

  const res = await axios.post(`${API}/upload`, fd, {
    headers: { "Content-Type": "multipart/form-data" },
    onUploadProgress: (e) => {
      if (onProgress && e.total) onProgress(Math.round((e.loaded / e.total) * 100));
    },
    maxContentLength: 50 * 1024 * 1024,
    maxBodyLength: 50 * 1024 * 1024,
  });

  return {
    file_id: res.data.file_id,
    url: res.data.url,
    file_name: res.data.file_name || compressed.name,
  };
};

export default uploadReceipt;
